import type { BrandConfig } from "../index";

/**
 * Proles Concept — katalog upominków firmowych ze znakowaniem.
 *
 * Wcześniej sklep chodził pod kryptonimem `gadzety`; klientka dostarczyła nazwę
 * i logotypy 01.09.2026 i od tego momentu marka nazywa się Proles Concept.
 * Identyfikator `gadzety` zostaje jeszcze jako alias w rejestrze marek, dopóki
 * inventory nie wjedzie z nowym NUXT_PUBLIC_BRAND — patrz komentarz w index.ts.
 *
 * Przekaz marki: „Twoja marka. Nasz koncept." — pomagamy firmom być widocznymi.
 * Świadomie NIE używamy sformułowania „agencja reklamowa": brzmi tradycyjnie
 * i stawia firmę w szeregu „kolejnych od gadżetów".
 *
 * Ten sklep NIE SPRZEDAJE: koszyk, checkout, płatności i konta są wyłączone flagą
 * NUXT_PUBLIC_CATALOG_ONLY, a konwersją jest zapytanie ofertowe. Stąd puste pola,
 * które u lavmaga i youlooka niosą treść — zwroty, logotypy płatności, próg
 * darmowej dostawy. Zostawione puste świadomie: UI je chowa, a wypełnienie ich
 * czymkolwiek obiecywałoby klientowi końcowemu funkcje, których tu nie ma.
 *
 * DANE FIRMY: adres, NIP i REGON dostała klientka 07.09.2026 i są już wpisane —
 * dzięki temu klauzula RODO na stronie kontaktu wskazuje administratora danych.
 * Adres kontaktowy klientka przesądziła 16.09.2026: kontakt@prolesconcept.pl.
 * Telefony są imienne (opiekunki Magda i Aneta, 03.11.2026) — ogólnego numeru
 * firmy nie ma. DO PRODUKCJI BRAKUJE JESZCZE działającej skrzynki pod tym
 * adresem (domena prolesconcept.pl nie jest podpięta) — dziś sklep chodzi na
 * adresie testowym.
 */
export const prolesBrand: BrandConfig = {
  id: "proles",
  name: "Proles Concept",
  currency: "PLN",
  // Sklep nie prowadzi sprzedaży, więc próg darmowej dostawy nie ma zastosowania.
  // 0 zamiast wartości z lavmaga, żeby pasek postępu nigdy nie obiecywał wysyłki.
  freeShippingThreshold: 0,
  // Domena QA; docelowa (produkcyjna) wciąż do ustalenia z klientką.
  // Fallback — w runtime nadpisuje to NUXT_SITE_URL z inventory.
  siteUrl: "https://proles-front-testing.easyitem.pl",
  // Obrazy idą z backendu tego sklepu, osobnego CDN nie ma. Pole nie jest dziś
  // przez nic czytane (poza definicjami brandów) — zostaje puste zamiast wskazywać
  // cudzy host.
  cdnHost: "",
  // Brak sprzedaży = brak zwrotów i odstąpienia od umowy. Puste wartości chowają
  // odpowiednie elementy UI.
  returnsUrl: "",
  returnsWithdrawalUrl: "",
  logo: {
    src: "/brands/proles/logo.svg",
    // Wersja odwrócona (biały napis) na ciemne tła — stopka jest grafitowa,
    // a podstawowy logotyp ma czarny „proles".
    srcInverted: "/brands/proles/logo-rev.svg",
    // Audyt strony głównej zgłosił logo w nagłówku jako obrazek bez opisu.
    alt: "Proles Concept",
  },
  // Dokument pod /regulamin-sklepu nosi tytuł „Regulamin serwisu
  // internetowego" — sklepu tu nie ma, więc i link tak się nie nazywa.
  termsLabel: "Regulamin serwisu",
  // Nowości, Techniki znakowania i linki statyczne nie mają podkategorii —
  // w menu mobilnym prowadzą prosto do swojej strony.
  mobileNavDirectLinks: true,
  // Jedyną zakładką strony głównej są nowości, więc „Ostatnio dodane"
  // powtarzałoby te same produkty.
  homeSkipDuplicateNewProducts: true,
  favicon: {
    // Sam znak graficzny, bez napisu — w 32x32 pełny logotyp byłby nieczytelny.
    // Rastry (04.11.2026) wygenerowane z tego SVG: favicon.ico 16/32/48 na
    // przezroczystym tle jak wektor, apple-touch-icon jak ikony PWA niżej
    // (iOS nie znosi przezroczystości — zamienia ją na czerń).
    svg: "/brands/proles/favicon.svg",
    ico: "/brands/proles/favicon.ico",
    appleTouchIcon: "/brands/proles/apple-touch-icon-180.png",
  },
  // Modal instalacji PWA (także instrukcja dla iOS) i /offline — własna ikona,
  // nie „L" lavmaga z /pwa-icons/.
  appIcon: "/brands/proles/pwa-icons/icon-192.png",
  footer: {
    // Dane rejestrowe z wizytówki przekazanej przez klientkę 07.09.2026.
    // Sprawdzone w wykazie podatników VAT (NIP 9441064583): podatnik czynny,
    // REGON i adres zgodne z wizytówką. Proles Concept to marka handlowa tej
    // samej działalności co dotychczasowy Proles — stąd wspólny NIP.
    address: "ul. Kolejowa 3",
    postalCode: "32-086",
    city: "Dziekanowice",
    country: "Polska",
    // Przesądzone 16.09.2026: „Na ten adres chcemy żeby były kierowane
    // wszelkie zapytania kontakt@prolesconcept.pl". Poprzednie
    // biuro@prolesconcept.pl było dopiero propozycją („raczej").
    // Domena prolesconcept.pl nadal nie jest podpięta — skrzynka musi
    // odbierać, zanim sklep wyjdzie z adresu testowego.
    email: "kontakt@prolesconcept.pl",
    // Spotkanie 30.10.2026: imienne skrzynki opiekunek do „Biura obsługi
    // klienta" na stronie kontaktu; telefony doszły 03.11.2026.
    serviceContacts: [
      { name: "Magda", email: "magda@prolesconcept.pl", phone: "570 011 437" },
      { name: "Aneta", email: "aneta@prolesconcept.pl", phone: "570 018 583" },
    ],
    // Telefon z wizytówki (+48 694 465 438) klientka WYKREŚLIŁA razem z
    // www.proles.com.pl i biuro@proles.com.pl — to kontakty starego Proles.
    // Ogólnego numeru firmy nie ma (są tylko numery opiekunek powyżej), więc
    // pole zostaje puste: stopka i strona kontaktu chowają ten wiersz.
    phone: "",
    copyrightName: "Proles Concept",
    createdBy: "contelizer.pl",
    // Działalność jednoosobowa, więc administratorem danych jest osoba fizyczna
    // — tak brzmi klauzula RODO na stronie kontaktu. Jeśli wpis w CEIDG ma
    // dłuższą nazwę firmy („Proles Robert Czajczyk" itp.), to jedyne miejsce
    // do poprawienia.
    legalName: "Robert Czajczyk",
    legalSeat: "Dziekanowicach",
    taxId: "944 106 45 83",
    regon: "122493844",
    // Godzin pracy biura klientka nie podała — pusty łańcuch chowa wiersz.
    serviceHours: "",
    // Działy główne katalogu PROD prolesconcept.pl (sitemapa, 29.10.2026),
    // w kolejności menu. Drzewo buduje EasyItem przy replikacji oferty; przy
    // kolejnych przebiegach istniejące kategorie są reużywane, więc ID w
    // adresach się nie zmieniają. Gdy EasyItem zbuduje drzewo od nowa (tak było
    // przy starcie prod: QA 1827–1840 → prod 3–16), poprawić tu ORAZ w bazie
    // MOD_BLOCKTOPMENU_ITEMS — z niego czytają menu i kafle działów na stronie
    // głównej, więc stare ID dają pusty /api/store/nav i znikają oba.
    //
    // Etykiety jak w menu. Slug „rozrywka-i-szkola" celowo z „ł" → „l": baza
    // ma „rozrywka-i-szkoa" (EasyItem gubi „ł" przy synchronizacji kategorii),
    // ale front rozwiązuje kategorię po ID z adresu, więc oba warianty działają,
    // a ten zostaje poprawny po naprawie slugów w bazie.
    links: [
      { label: "Do picia", href: "/3-do-picia" },
      { label: "Torby i plecaki", href: "/4-torby-i-plecaki" },
      { label: "Elektronika", href: "/5-elektronika" },
      { label: "Do pisania", href: "/6-do-pisania" },
      { label: "Biuro", href: "/7-biuro" },
      { label: "Podróż", href: "/8-podroz" },
      { label: "Parasole i peleryny", href: "/9-parasole-i-peleryny" },
      { label: "Breloki", href: "/10-breloki" },
      { label: "Wypoczynek", href: "/11-wypoczynek" },
      { label: "Rozrywka i szkoła", href: "/12-rozrywka-i-szkola" },
      { label: "Dom", href: "/13-dom" },
      { label: "Narzędzia", href: "/14-narzedzia" },
      { label: "Tekstylia", href: "/15-tekstylia" },
      { label: "Zestawy upominkowe", href: "/16-zestawy-upominkowe" },
    ],
  },
  // Profile podane przez klientkę (mail 27.10.2026). Bez TikToka — proles go nie ma.
  social: {
    instagram: "https://www.instagram.com/proles.concept/",
    facebook: "https://www.facebook.com/people/Proles-Concept/61594439609512/",
  },
  seo: {
    titleTemplate: "%s · Proles Concept",
    defaultTitle: "Proles Concept — budujemy widoczność Twojej marki",
    defaultDescription:
      "Upominki firmowe i gadżety reklamowe ze znakowaniem. Od wyboru produktu, przez projekt, po profesjonalne znakowanie — zajmujemy się całością.",
    // Logotyp jako obraz OG do czasu dedykowanej grafiki: lepszy niż brak
    // podglądu przy udostępnianiu linku.
    ogImage: "/brands/proles/logo.svg",
    // Turkus ze znaku firmowego — pasek adresu na mobile dostaje kolor marki.
    themeColor: "#64bfaa",
  },
  // Ceny NETTO z dopiskiem „+ VAT 23%" (uwaga klientki 20.10.2026). Netto daje
  // backend: grupy klientów proles (Odwiedzający, Gość, Klient) mają wyświetlanie
  // cen „bez podatku" (price_display_method = 1). Wszystkie 4663 produkty mają
  // stawkę 23% (PL Standard Rate). Dopisek MUSI iść w parze z ustawieniem grup —
  // bez niego cena brutto byłaby podpisana jako netto.
  priceTaxNote: "+ VAT 23%",
  // Cookie zgody bez cudzej marki w nazwie — wymienia je polityka prywatności
  // prolesconcept.pl (29.10.2026). Baner na prod startuje dopiero z GTM, więc
  // nikt nie traci zapisanej decyzji.
  consentCookieName: "cookie_consent",
  // Breadcrumb jak menu: „Tekstylia / Inne tekstylia i odzież", a nie sama
  // podkategoria — dział nadrzędny ginął i nie zgadzał się z nawigacją.
  pdpBreadcrumbNavPath: true,
  // Spotkanie 30.10.2026: „domyślnie rozwinięte podkategorie w drzewie, jak
  // ktoś jest w głównej kategorii" — dział był zaznaczony, ale zwinięty.
  filterTreeExpandSelected: true,
  // Galeria: duże zdjęcie + miniatury zamiast siatki 2×3 kafli 3:4 — przy
  // packshotach na białym tle siatka miała 1680 px wysokości przy 1440 px
  // szerokości ekranu (2640 px przy 1920), a kolumna z ceną 643 px (29.10.2026).
  pdpGalleryLayout: "main-thumbs",
  // Wybór koloru na karcie i w szybkim podglądzie przełącza zdjęcia na ten
  // kolor (+ wspólne). EasyItem przypina zdjęcia do kolorów (color_ids).
  pdpGalleryFollowsColor: true,
  // Zgłoszenie 05.11.2026: „zmiana koloru nie powoduje zmiany nazwy oraz
  // szczegółów produktu i skład” — nazwa i SKU z EasyItem to kombinacja domyślna.
  pdpDetailsFollowVariant: true,
  // 03.11.2026: „wszystkie listingi na desktopie mają mieć 4 w rzędzie" —
  // rekomendacje na karcie miały 3, listingi 3 (4 od 1800 px), home 4.
  pdpRecommendationColumns: 4,
  // Adres karty pokazuje wybrany rozmiar (`?rozmiar=L`) — udostępniony link
  // otwiera ten wariant, a nie domyślny („…-xxl").
  pdpSizeInUrl: true,
  // Zgłoszenie 30.10.2026: „kolory do kolorów — trzeba zrobić mapę kolorów, bo
  // żaden dostawca nie dostarcza hexów". Przy nazwie koloru kółko z mapy nazw.
  colorSwatches: true,
  // PWA (instalacja + tryb offline) włącza inventory w runtime:
  // NUXT_PUBLIC_PWA_ENABLED=true ORAZ NUXT_PUBLIC_PWA_ROLLOUT_PERCENT=100. Push
  // nie ma tu czego obsługiwać — soft-ask odpala się po dodaniu do koszyka
  // i logowaniu, a katalog nie ma ani jednego, ani drugiego.
  pwa: {
    name: "Proles Concept — upominki firmowe ze znakowaniem",
    shortName: "Proles",
    description:
      "Katalog upominków firmowych ze znakowaniem. Wybierz produkt i zapytaj o wycenę.",
    // Znak „p" w turkusie (#64bfaa) na ciemnym tealu marki (#104646) — jak
    // ciemne posty na Instagramie i stopka. Wygenerowane z favicon.svg
    // 04.11.2026; maskable ma znak w ~50% płótna, czyli w strefie bezpiecznej
    // (koło 80%). Katalog własny — /pwa-icons/* to zestaw lavmaga.
    icons: [
      { src: "/brands/proles/pwa-icons/icon-192.png", sizes: "192x192", type: "image/png", purpose: "any" },
      { src: "/brands/proles/pwa-icons/icon-512.png", sizes: "512x512", type: "image/png", purpose: "any" },
      { src: "/brands/proles/pwa-icons/icon-maskable-512.png", sizes: "512x512", type: "image/png", purpose: "maskable" },
    ],
    // Katalog nie ma koszyka ani konta — wspólne skróty „Mój koszyk" (/cart)
    // i „Moje konto" (/account) prowadziłyby do 404 (catalog-only.global.ts).
    // Zamiast nich: lista zapytania (odpowiednik koszyka), nowości i kontakt
    // z opiekunkami.
    shortcuts: [
      { name: "Lista zapytania", url: "/lista-zapytania" },
      { name: "Nowości", url: "/nowosci" },
      { name: "Kontakt", url: "/contact" },
    ],
    // Upominki firmowe dla firm, nie moda — „fashion" i „lifestyle" z domyślnych
    // kategorii opisywałyby inny sklep.
    categories: ["shopping", "business"],
  },
  // Brak płatności — sklep nie ma checkoutu, więc żadne logo bramki nie ma tu racji bytu.
  paymentLogos: [],
  search: {
    // Frazy sprawdzone w wyszukiwarce QA 16.10.2026: kubek 324, długopis 641,
    // torba 349 wyników. Wcześniej strona 404 podpowiadała odzież.
    examples: "kubek, długopis, torba",
    // Overlay z lupki układał wyniki po 4 w rzędzie, listing i /search — po 3;
    // przy czterech kartach z „Zapytaj o wycenę" nazwy ucinały się do kilku
    // liter (Asana 1219566675747702).
    overlayColumns: 3,
  },
  // Strona „O nas” (app/pages/about.vue, filozofia.stat1): „od 50 — sztuk
  // minimalnego nakładu". Lista zapytania i formularz wyceny startują od tej
  // liczby i nie przyjmują mniej (Asana 1219579525880170). Zmieniasz tu —
  // zmień też tamtą obietnicę.
  quoteMinQuantity: 50,
};
